import classNames from 'classnames';
import React, { Component, ComponentType } from 'react';

import { mainEpisodeClick } from '@wix/bi-logger-podcast/v2';

import { IPodcastEpisode } from '@wix/podcasts-common/dist/podcast';
import { EpisodesGridTextPosition } from '@wix/podcasts-common/dist/settings';

import { biLogger } from '../../../../../client/bi';
import {
  IWithI18nProps,
  withI18n,
} from '../../../../../components/context/i18n';
import {
  IWithSettingsProps,
  withSettings,
} from '../../../../../components/context/settings';
import { IWithTPAProps, withTPA } from '../../../../../components/context/tpa';
import {
  EpisodeLink,
  navigateToEpisodePage,
} from '../../../../../components/nav';
import { ImgCover, ImgCoverStyle } from '../../../../../components/ui/img';

import { normalizeImageUrl } from '../../../../../lib/utils/image';

import styles from './EpisodesGridItem.scss';
import getStyles, { IEpisodesGridItemStyles } from './EpisodesGridItem.styles';
import PlayIcon from '../../../../../components/ui/icons/PlayIcon';
import DateTime from '../../../../../components/ui/DateTime';

interface IOwnProps {
  podcastImage: string;
  episode: IPodcastEpisode;
  isLatest: boolean;
  shouldFocus: boolean;
}

interface IEpisodeGridItemProps
  extends IWithSettingsProps,
    IWithI18nProps,
    IWithTPAProps,
    IOwnProps {}

class EpisodesGridItem extends Component<IEpisodeGridItemProps> {
  linkRef = React.createRef<HTMLAnchorElement>();

  componentDidMount(): void {
    if (this.props.shouldFocus) {
      this.linkRef.current?.focus?.();
    }
  }

  handleEpisodeLinkClick = () => {
    biLogger.report(
      mainEpisodeClick({
        episodeId: this.props.episode.id,
      }),
    );
  };

  renderImageContainer(dynamicStyle: IEpisodesGridItemStyles) {
    const { podcastImage, episode } = this.props;

    return (
      <div
        className={classNames(
          styles.imageContainer,
          dynamicStyle.imageContainer,
        )}
      >
        <ImgCover
          data-hook="main.episodes.episode.image"
          coverStyle={ImgCoverStyle.SQUARE}
          src={normalizeImageUrl(episode.image || podcastImage)}
          title={episode.title}
        />
      </div>
    );
  }

  renderTitle(dynamicStyle: IEpisodesGridItemStyles) {
    const { episode } = this.props;

    return (
      <h2
        data-hook="main.episodes.episode.title"
        className={classNames(styles.episodeTitle, dynamicStyle.episodeTitle)}
      >
        {episode.title}
      </h2>
    );
  }

  renderLatestTag(dynamicStyle: IEpisodesGridItemStyles) {
    const { t, isLatest, settings } = this.props;
    const mainSettings = settings.main;

    return (
      isLatest &&
      mainSettings.showLatestEpisodeText && (
        <div
          data-hook="main.episodes.episode.latest-label"
          className={classNames(styles.episodeLabel, dynamicStyle.episodeLabel)}
        >
          {t('pages.main.episode-latest-label')}
        </div>
      )
    );
  }

  renderMetaInfo(dynamicStyle: IEpisodesGridItemStyles) {
    const { episode, settings } = this.props;

    const mainSettings = settings.main;

    return (
      (mainSettings.showEpisodeDate || mainSettings.showEpisodeDuration) && (
        <div
          className={classNames(
            styles.episodeMetaInfo,
            dynamicStyle.episodeMetaInfo,
          )}
        >
          {mainSettings.showEpisodeDate && (
            <DateTime
              type="date"
              value={episode.date}
              dataHook="main.episodes.episode.date"
              className={styles.episodeDate}
            />
          )}
          {mainSettings.showEpisodeDate && mainSettings.showEpisodeDuration && (
            <span
              aria-hidden="true"
              role="presentation"
              className={styles.episodeDotDivider}
            >
              &nbsp;|&nbsp;
            </span>
          )}
          {mainSettings.showEpisodeDuration && (
            <DateTime
              type="duration"
              value={episode.duration}
              dataHook="main.episodes.episode.duration"
              className={styles.episodeDuration}
            />
          )}
        </div>
      )
    );
  }
  redirectToEpisodeOnClick = () => {
    this.handleEpisodeLinkClick();
    navigateToEpisodePage(this.props.episode.id, this.props.episode.slug);
  };

  renderDescription(dynamicStyle: IEpisodesGridItemStyles) {
    const { episode, settings } = this.props;

    const mainSettings = settings.main;

    return (
      <div className={styles.episodeDescriptionWrapper}>
        <div
          data-hook="main.episodes.episode.description"
          className={classNames(
            styles.episodeDescription,
            dynamicStyle.episodeDescription,
          )}
        >
          {mainSettings.showEpisodeDescription ? episode.description : null}
        </div>
      </div>
    );
  }

  renderListenNow(dynamicStyle: IEpisodesGridItemStyles) {
    const { settings, t } = this.props;

    return (
      settings.main.episodesGridTextPosition ===
        EpisodesGridTextPosition.ON && (
        <div
          data-hook="main.episodes.episode.listen-now"
          className={classNames(
            styles.episodeListenNow,
            dynamicStyle.episodeListenNow,
          )}
        >
          <PlayIcon size={32} className={styles.episodeListenNowIcon} />
          {t('pages.main.episode-listen-now')}
        </div>
      )
    );
  }

  render() {
    const { episode, settings, tpa } = this.props;
    const dynamicStyles = getStyles({
      deviceType: tpa.params.deviceType,
      colors: settings.colors.main,
      fonts: settings.fonts.main,
      settings: settings.main,
    });

    const rootClassName =
      settings.main.episodesGridTextPosition === EpisodesGridTextPosition.ON
        ? styles.episodeTextOn
        : styles.episodeTextBelow;

    return (
      <li
        className={classNames(rootClassName, dynamicStyles.container)}
        data-hook="main.episodes.episode"
        onClick={this.redirectToEpisodeOnClick}
        role="group"
      >
        <div
          className={classNames(
            styles.episodeInner,
            dynamicStyles.episodeInner,
          )}
        >
          {this.renderImageContainer(dynamicStyles)}
          {this.renderLatestTag(dynamicStyles)}
          <div
            className={classNames(
              styles.infoContainer,
              dynamicStyles.infoContainer,
            )}
          >
            <EpisodeLink
              id={episode.id}
              slug={episode.slug}
              onClick={this.handleEpisodeLinkClick}
              linkRef={this.linkRef}
            >
              {this.renderTitle(dynamicStyles)}
            </EpisodeLink>
            {this.renderMetaInfo(dynamicStyles)}
            {this.renderDescription(dynamicStyles)}
          </div>
          {this.renderListenNow(dynamicStyles)}
        </div>
      </li>
    );
  }
}

export default withI18n(
  withTPA(withSettings(EpisodesGridItem)),
) as ComponentType<IOwnProps>;
